import "phoenix_html";
import { Socket } from "phoenix";
import { LiveSocket } from "phoenix_live_view";
import ClipboardJS from "clipboard";
import _ from "lodash";

import moment from "moment";
import "moment/locale/fr";
import "moment/locale/en-gb";
import "moment/locale/de";
import "moment/locale/es";

import Vue from "vue";

import DevExpressFrMessages from "devextreme/localization/messages/fr.json";
import DevExpressEnMessages from "devextreme/localization/messages/en.json";
import DevExpressEsMessages from "devextreme/localization/messages/es.json";
import DevExpressDeMessages from "devextreme/localization/messages/de.json";
import * as DevExpressLocalization from "devextreme/localization";
import DevExpressConfig from "devextreme/core/config";

import * as Sentry from "@sentry/browser";

import * as API from "./framework/api";
import { initPrintImage } from "./framework/print-image";
import * as ScrollSpy from "./framework/scrollspy";
import { initWindowOpen } from "./framework/window-open";
import * as Layout from "./layout";
import * as Toast from "./framework/components/toast";
import { createRoutes, match } from "./router";
import Alpine from "./alpine";
import { initPolyfills } from "./polyfills";
import { installJqueryMigrateSentryBridge } from "./utils/jquery_migrate_sentry";

/* global jQuery, _money, _env */

/**
 * This is a drop in replacement for the native jQuery trigger method. It will
 * trigger the event natively, instead of using jQuery's event system. This is
 * useful for triggering events that can be handled both by jQuery and native
 * code or other frameworks such as Vue / Alpine.
 *
 * See https://aworldforus.slite.com/app/docs/mrA8Ype8nklIYo#c1265276
 */
jQuery.fn.triggerNative = function (type, detail) {
  this.each(function () {
    this.dispatchEvent(new CustomEvent(type, { bubbles: true, detail }));
  });
  return this;
};

/**
 * We are doing feature detection instead of browser detection, because it's
 * more reliable and future-proof. We are using the `HTMLDialogElement` to
 * detect if the browser supports the `<dialog>` element. If it doesn't, we
 * show a warning to the user.
 *
 * Future requirements may be added to this if.
 */
if (typeof HTMLDialogElement === "undefined") {
  document.querySelector(".js-legacy-browser").classList.remove("has-hidden");
}

if (["staging", "prod"].includes(window._platform)) {
  Sentry.init({
    dsn: "https://227f1c574eb84a91bfca3ce2b1525f7e@o61992.ingest.sentry.io/5891526",
    sampleRate: 0.05, // Rate of errors sent
    environment: window._platform,
    release: window._release_version,
  });
  const scope = Sentry.getCurrentScope();
  scope.setUser({ id: window._current_academy_id, email: window._current_academy_email });
  scope.setTag("request-id", window._request_id);

  if (window._jqueryMigrateSentry === true) {
    installJqueryMigrateSentryBridge({
      jQuery,
      getStorage: () => window.localStorage,
      captureMessage: (message, context) => Sentry.captureMessage(message, context),
      releaseVersion: window._release_version,
    });
  }
}

import { Hooks } from "./liveview/liveview_hooks";

const csrfToken = document.querySelector("meta[name='csrf']").getAttribute("content");
const phxprofToken = window.getPhxProfToken?.();

const liveSocket = new LiveSocket("/live", Socket, {
  // A function, so that each LiveView join reads the settings as they are now: after a live navigation,
  // the settings saved since the page load would otherwise be missing.
  params: () => ({
    _csrf_token: csrfToken,
    _phxprof_token: phxprofToken,
    settings: JSON.parse(localStorage.getItem("liveview-storage") || "{}"),
  }),
  hooks: Hooks,
  dom: {
    onBeforeElUpdated: (fromEl, toEl) => {
      if (fromEl.tagName === "DIALOG") {
        Array.from(fromEl.attributes).forEach((attr) => {
          toEl.setAttribute(attr.name, attr.value);
        });

        return false;
      }

      return true;
    },
  },
  // When the websocket loses the connection, try to reconnect after a longer delay
  // than the default one.
  reconnectAfterMs(tries) {
    const backoff = [10, 50, 200, 1000, 5000, 10000][tries - 1] || 30000;

    // Add "Jitter": Add a random amount up to 10 seconds
    // This ensures clients don't hit the server at once in the event where a deployment or pod restart happens
    return backoff + Math.floor(Math.random() * 10000);
  },
});
liveSocket.connect();
window.liveSocket = liveSocket;

window.addEventListener("phx:live-focus", ({ detail: details }) => {
  let selector = details.selector;

  document.querySelector(selector).focus();
});

DevExpressLocalization.loadMessages(DevExpressDeMessages);
DevExpressLocalization.loadMessages(DevExpressEnMessages);
DevExpressLocalization.loadMessages(DevExpressEsMessages);
DevExpressLocalization.loadMessages(DevExpressFrMessages);
DevExpressLocalization.locale(window._locale);
// DevExtreme's translations overrides
DevExpressLocalization.loadMessages({
  fr: {
    "dxDataGrid-emptyHeaderWithColumnChooserText": "Utilisez {0} pour afficher les colonnes",
    "dxDataGrid-emptyHeaderColumnChooserText": "Choisir les colonnes",
    "dxPager-ariaLabel": "Navigation entre les pages",
    "dxPager-pageSize": "Éléments par page : {0}",
  },
});
DevExpressConfig({
  defaultCurrency: _money.code,
  defaultUseCurrencyAccountingStyle: false,
  licenseKey:
    "ewogICJmb3JtYXQiOiAxLAogICJjdXN0b21lcklkIjogIjgyZDIzMGQ0LTVmMjAtNGY2My1iYzI0LWMwZTIzOGQ5NzQxNiIsCiAgIm1heFZlcnNpb25BbGxvd2VkIjogMjQxCn0=.j55eBoprQFYOMb6UJCNKYwqyt/yfcEXBSKyLGmTG5iBOIyy5z5mz1x9r6cDCfbxyKEt31dNMCqMvEAcryo6fvRrBdEdHAcIz0Fzdd10/ZJ4hRyCJ9/TBQD5nnQ0tRs++FFf4eQ==",
});

import "./abandon";
import "./activity";
import "./attendance";
import "./catalog_config";
import "./company";
import "./contacts";
import "./crm";
import "./custom_diplomas";
import "./customer";
import "./data_pages";
import "./document";
import "./evaluation";
import "./finance";
import "./form";
import "./framework";
import "./fundae";
import "./funding_agency";
import "./html_doc";
import "./import/import_file_input";
import "./instructor";
import "./learning_path";
import "./liveview/liveview_events";
import "./module";
import "./module_category";
import "./partial_absence";
import "./pdf_importer";
import "./program_agreement";
import "./scorm";
import "./signing";
import "./trainee";
import "./training_session";
import "./component/";
import "./academy";
import "./utils/select_all";

/**
 * Initialise a client-side router that will match routes against the current
 * location pathname and initialise the corresponding module.
 *
 * These JavaScript modules are imported dynamically so that they are only loaded
 * when needed. This allows to reduce the initial load time of the application,
 * and run the generic JavaScript code as fast as possible.
 *
 * ## `init` function
 *
 * Modules may export an `init` function that will be called automatically when
 * the route is matched. This function takes an object as argument, containing
 * the parameters extracted from the route.
 *
 * The generic rule is "don't use this function unless you need to". In most
 * modules, using top-level JavaScript code is enough. Some reasons to use
 * `init` could be:
 * - you need to access the route parameters,
 * - you need to use `await` during the initialisation of the module (prefer
 *   declaring `init` to using a top-level `await`),
 * - the module is fairly long or complex, and explicitly declaring where it
 *   starts helps understanding it.
 *
 * @returns {Promise<void>} A promise resolving once either a module has been
 *   matched and initialized, or no module has been matched.
 */
async function initRouter() {
  // A resource form managing its custom fields: the popup adding or editing one, and their drag
  // and drop.
  const loadCustomFieldManagement = () =>
    Promise.all([import("./custom_fields/custom_field_popup"), import("./custom_fields/custom_field_reorder")]);

  const routes = createRoutes({
    "/academy/edit": () => import("./routes/academy/edit"),
    "/academy": () => import("./routes/academy/edit"),
    "/academy/subscribe": () => import("./routes/academy/subscribe"),
    // The bare path is the convocations tab, which is the automations landing page.
    "/academy/automations": () => import("./routes/automation/auto_send_form"),
    "/academy/automations/convocation": () => import("./routes/automation/auto_send_form"),
    "/academy/automations/invoice": () => import("./routes/automation/auto_send_form"),
    "/academy/automations/dashboard*": () => import("./routes/automation_dashboard/timeline"),
    "/training_sessions/:trainingSessionId/automations*": () => import("./routes/automation_dashboard/timeline"),
    "/academies/new": () => import("./routes/academies/new"),
    "/training_sessions/:trainingSessionId/trainee_support/export_accrochage*": () =>
      import("./routes/trainee_support/export_accrochage"),
    "/corrective_actions": () => import("./routes/corrective_actions/corrective_actions"),
    "/crm": async () => {
      await import("./routes/crm/kanban");
      await import("./routes/crm/dashboard");
    },
    "/crm/frozen": async () => {
      await import("./routes/crm/kanban");
      await import("./routes/crm/dashboard");
    },
    "/crm/registrations": () => import("./routes/registration/registrations"),
    "/crm/recyclage": () => import("./routes/crm/recyclages"),
    "/crm/registrations/:companyId/edit_company": async () => {
      await import("./company/billing_address");
      await import("./routes/registration/merger");
    },
    "/crm/registrations/:registrationId/edit_trainee": () => import("./routes/registration/merger"),
    "/dashboard": () => import("./routes/dashboard/index"),
    "/dashboard/agenda": () => import("./routes/agenda"),
    "/evaluations/:id": () => import("./routes/evaluations/distribute-skills"),
    "/help/chat": () => import("./routes/help/chat"),
    "/help": () => import("./routes/help/help"),
    "/help/*": () => import("./routes/help/help"),
    "/improvement_axes": () => import("./routes/improvement_axis/improvement_axes"),
    "/incidents": () => import("./routes/incident/incidents"),
    "/incidents/*": () => import("./routes/incident/incident"),
    "/instructor_cockpit/agenda": () => import("./routes/instructor_cockpit/agenda"),
    "/internal/showcase/table": () => import("./routes/internal/showcase_table"),
    "/internal/showcase/form": () => import("./routes/internal/showcase_form"),
    "/internal/showcase": () => import("./routes/internal/showcase"),
    "/invoices/:invoiceId/edit": () => import("./routes/invoices/:invoice_id/edit"),
    "/invoices/:invoiceId/preview*": () => import("./routes/invoices/:invoice_id/preview"),
    "/invoices/new": () => import("./routes/invoices/new"),
    "/modules/:moduleId/email_markers*": () => import("./routes/modules/:moduleId/email_markers*"),
    "/finance/analysis/training_sessions": () => import("./routes/analysis/finance_sessions"),
    "/finance/analysis/abandons": () => import("./routes/analysis/finance_abandons"),
    "/finance/analysis/customers": () => import("./routes/analysis/finance_customers"),
    "/finance/analysis/instructors": () => import("./routes/analysis/finance_instructors"),
    "/finance/analysis/trainees": () => import("./routes/analysis/finance_trainees"),
    "/finance/crm": () => import("./routes/finance/crm"),
    "/finance/invoices": () => import("./routes/finance/invoices"),
    "/finance/supplier_invoices": () => import("./routes/finance/supplier_invoices"),
    "/supplier_invoices/new": () => import("./routes/supplier_invoices/form"),
    "/supplier_invoices/:supplierInvoiceId/edit": () => import("./routes/supplier_invoices/form"),
    "/finance/quotations": () => import("./routes/finance/quotations"),
    "/finance/marketplace": () => import("./routes/finance/marketplace"),
    "/finance/fundae": () => import("./routes/finance/fundae"),
    "/finance/training_sessions/:id": () => import("./routes/finance/ts_invoices_chart"),
    "/finance/training_sessions/:id/charges": () => import("./routes/finance/ts_invoices_chart"),
    "/modules/:moduleId/edit": () => import("./routes/subsessions/subsessions_selector"),
    "/prevention_passport": () => import("./routes/prevention_passport"),
    "/programs": () => import("./routes/programs/filters"),
    "/programs/blocks": () => import("./routes/programs/filters_pedagogical_blocks"),
    "/programs/blocks/frozen": () => import("./routes/programs/filters_pedagogical_blocks"),
    "/programs/blocks/new": () => import("./routes/programs/program"),
    "/programs/blocks/:pedagogicalBlockId": () => import("./routes/programs/program"),
    "/programs/blocks/:pedagogicalBlockId/edit": () => import("./routes/programs/program"),
    "/training_sessions/:trainingSessionId/program_blocks/:pedagogicalBlockId/edit": () => import("./routes/programs/program"),
    "/programs/frozen": () => import("./routes/programs/filters"),
    "/programs/new": () => import("./routes/programs/program"),
    "/programs/index_tabular": () => import("./routes/programs/tabular"),
    "/programs/:programId/edit": () => import("./routes/programs/program"),
    "/programs/:programId/elearning": () => import("./routes/programs/elearning_show"),
    "/programs/:programId/elearning/new": () => import("./routes/programs/elearning_new"),
    "/programs/:programId/add_program_blocks": () => import("./routes/programs/program"),
    "/programs/:programId": () => import("./routes/programs/program"),
    "/programs/:programId/dashboard": () => import("./routes/programs/dashboard"),
    "/inspector/:trainingSessionId": () => import("./routes/programs/program"),
    "/edof": () => import("./routes/programs/edof"),
    "/quality/form_templates": () => import("./routes/quality/form_templates"),
    "/form_templates": () => import("./routes/form_templates"),
    "/quality/:mode": () => import("./routes/quality/:mode"),
    "/signing_requests/:mode": () => import("./routes/signing_requests/index"),
    "/scorms*": () => import("./routes/scorms"),
    "/certifiers": () => import("./routes/certifiers"),
    "/companies": () => import("./routes/companies"),
    "/companies/new": async () => {
      await import("./company/billing_address");
      await loadCustomFieldManagement();
    },
    // Also the page an invalid edit form renders at
    "/companies/:companyId": async () => {
      await import("./routes/companies/show");
      await loadCustomFieldManagement();
    },
    "/companies/:companyId/edit": async () => {
      await import("./company/billing_address");
      await import("./routes/companies/edit");
      await loadCustomFieldManagement();
    },
    "/companies/:companyId/documents": () => import("./routes/companies/documents"),
    "/companies/:companyId/emails": () => import("./routes/companies/emails"),
    "/companies/:companyId/opportunities": () => import("./routes/companies/opportunities"),
    "/companies/:companyId/sessions": () => import("./routes/companies/sessions"),
    "/companies/:companyId/trainees": () => import("./routes/companies/trainees"),
    "/competency_verdicts": () => import("./routes/competency_verdicts"),
    "/rooms": () => import("./routes/rooms"),
    "/rooms/new": loadCustomFieldManagement,
    // Also the page an invalid edit form renders at
    "/rooms/:roomId": loadCustomFieldManagement,
    "/rooms/:roomId/edit": loadCustomFieldManagement,
    "/trainees": () => import("./routes/trainees"),
    // Avoid `/:traineeId` catching the `/import` page
    "/trainees/import": () => Promise.resolve(),
    "/trainees/new": loadCustomFieldManagement,
    // Also the page an invalid edit form renders at
    "/trainees/:traineeId": loadCustomFieldManagement,
    "/trainees/:traineeId/edit": async () => {
      await import("./routes/trainees/edit");
      await loadCustomFieldManagement();
    },
    "/instructors": () => import("./routes/instructors"),
    // Avoid `/:instructorId` catching the `/import` page
    "/instructors/import": () => Promise.resolve(),
    "/instructors/new": loadCustomFieldManagement,
    // Also the page an invalid edit form renders at
    "/instructors/:instructorId": loadCustomFieldManagement,
    "/instructors/:instructorId/edit": async () => {
      await import("./routes/instructors/edit");
      await loadCustomFieldManagement();
    },
    "/funding_agencies": () => import("./routes/funding_agencies"),
    "/funding_agencies/new": loadCustomFieldManagement,
    // Also the page an invalid edit form renders at
    "/funding_agencies/:fundingAgencyId": loadCustomFieldManagement,
    "/funding_agencies/:fundingAgencyId/edit": async () => {
      await import("./routes/funding_agencies/edit");
      await loadCustomFieldManagement();
    },
    // Avoid `/:trainingSessionId` catching the `/frozen` and `/new` pages
    "/training_sessions": async () => {
      await import("./routes/training_sessions/kanban");
      await import("./routes/training_sessions/dashboard");
      await import("./routes/training_sessions/list");
    },
    "/training_sessions/frozen": async () => {
      await import("./routes/training_sessions/kanban");
      await import("./routes/training_sessions/dashboard");
      await import("./routes/training_sessions/list");
    },
    "/crm/replace_training_session/:customerId": async () => {
      await import("./routes/training_sessions/kanban");
      await import("./routes/training_sessions/dashboard");
    },
    "/training_sessions/new": loadCustomFieldManagement,
    "/training_sessions/:trainingSessionId/edit": loadCustomFieldManagement,
    "/training_sessions/:trainingSessionId/abandons": () => import("./routes/training_sessions/abandons/index"),
    "/training_sessions/:trainingSessionId/attendance": () => import("./routes/training_sessions/attendance/followup"),
    "/training_sessions/:trainingSessionId/attendance/automatic": () => import("./routes/training_sessions/attendance/automatic"),
    "/training_sessions/:trainingSessionId/attendance/manual_sign": () => import("./routes/training_sessions/attendance/manual_sign"),
    "/training_sessions/:trainingSessionId/dates": () => import("./routes/training_sessions/dates"),
    "/training_sessions/:trainingSessionId/cfa_contracts": () => import("./routes/training_sessions/cfa_contracts"),
    "/training_sessions/:trainingSessionId/extranet/elearning": () =>
      Promise.all([import("./routes/subsessions/subsessions_selector"), import("./routes/training_session_extranet/show_elearning")]),
    "/training_sessions/:trainingSessionId/extranet/positioning": () =>
      Promise.all([
        import("./routes/subsessions/subsessions_selector"),
        import("./routes/training_session_extranet/module_tool_page"),
      ]),
    "/training_sessions/:trainingSessionId/live_quizzes": () =>
      Promise.all([
        import("./routes/subsessions/subsessions_selector"),
        import("./routes/training_session_extranet/module_tool_page"),
      ]),
    "/training_sessions/:trainingSessionId/extranet/pedagogical_resources": () =>
      Promise.all([
        import("./routes/subsessions/subsessions_selector"),
        import("./routes/training_session_extranet/module_tool_page"),
      ]),
    "/training_sessions/:trainingSessionId/customers": () => import("./routes/training_sessions/customers/index"),
    "/training_sessions/:trainingSessionId/customers/new": loadCustomFieldManagement,
    "/training_sessions/:trainingSessionId/customers/new/individual": loadCustomFieldManagement,
    "/training_sessions/:trainingSessionId/customers/:customerId/edit": loadCustomFieldManagement,
    "/training_sessions/:trainingSessionId/training_session_instructors/new": loadCustomFieldManagement,
    "/training_sessions/:trainingSessionId/training_session_instructors/:trainingSessionInstructorId/edit": loadCustomFieldManagement,
    "/training_sessions/:trainingSessionId/program": () => import("./routes/programs/index"),
    "/registration_config/:trainingSessionId": () => import("./routes/registration_config/index"),
    "/registration_config": () => import("./routes/registration_config/index"),
    "/training_sessions/:trainingSessionId/communications/email_history": () => import("./routes/communications/email_history"),
    "/training_sessions/:trainingSessionId/communications/scheduled_emails": () => import("./routes/communications/scheduled_emails"),
    "/training_sessions/:trainingSessionId/convocations": () => import("./routes/automation/auto_send_form"),
    "/training_sessions/:trainingSessionId/trainee_support": () => import("./routes/training_session/trainee_support"),
    "/training_sessions/:trainingSessionId/register_trainee/:traineeId": () => import("./routes/registration/merger"),
    "/training_sessions/:trainingSessionId/classbook/:classbookId": () => import("./routes/classbook/classbook"),
    "/training_sessions/:trainingSessionId/live_quizzes/:moduleId/followup": () =>
      import("./routes/live_quiz_session/live_quiz_content_renderer"),
    "/user/custom_badges*": () => import("./routes/user/custom_badges"),
    "/user/custom_diplomas*": () => import("./routes/user/custom_diplomas"),
    "/catalog_registration*": () => import("./routes/catalog_registration/index"),
    "/programs/:programId/catalog_registration": () => import("./routes/catalog_registration/index"),
    "/user/custom_documents": () => import("./routes/user/custom_documents"),
    "/user/custom_documents/*": () => import("./routes/user/custom_documents/edit"),
    "/user/email_templates": () => import("./routes/user/email_templates"),
    "/user/email_templates/*": () => import("./routes/user/email_templates/edit"),
    "/toolbox": () => import("./routes/toolbox"),
    "/bank/reconciliation": () => import("./routes/bank_reconciliation/index"),
    "/finance/company-portal": () => import("./routes/finance/company_portal_report"),
    "/inspector/:trainingSessionId/trainees": () => import("./routes/fundae_inspector_view/email_history_table.js"),
    "/certified_programs/:programId": () => import("./routes/certified_programs/certified_programs_datagrids.js"),
  });

  const routerMatch = match(routes, location.pathname);
  if (routerMatch === undefined) return;

  const { returnValue, params } = routerMatch;
  const maybeModule = await returnValue;
  if (typeof maybeModule === "object" && typeof maybeModule.init === "function") {
    maybeModule.init({ params });
  }
}

moment.locale(window._locale);

// SETUP AJAX CSRF
window.ajax = function (data) {
  data.headers = {
    "x-csrf-token": API.getCSRFToken(),
    "x-phxprof-token": window.getPhxProfToken?.(),
  };

  return $.ajax(data);
};

// AT EACH LOAD
document.addEventListener("DOMContentLoaded", () => {
  // No await for material to be loaded, it's not mandatory
  clipboards();
  initPrintImage();
  initWindowOpen();

  initRouter().then(() => {
    // Start Alpine after the router so that routes have the time to register their own Alpine store / data ...
    Alpine.start();
  });

  initPolyfills();
  Layout.init();
  Toast.consumeFlashData();
  Toast.initLiveViewListener();

  if (_env != "prod") initDevReport(_env);

  ScrollSpy.init();
});

function clipboards() {
  new ClipboardJS(".clipboard");
}

// GET QUERY PARAMETER BY NAME
window.getQueryParameter = function (name) {
  let url = window.location.href;
  name = name.replace(/[[\]]/g, "\\$&");
  let regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)"),
    results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return "";
  return decodeURIComponent(results[2].replace(/\+/g, " "));
};

async function initDevReport() {
  const DevReport = await import("./dev_reports");
  DevReport.init(_env);
}

Vue.filter("dateFormat", function (value) {
  return moment(value).format("dddd DD MMMM");
});

Vue.filter("dateFromNow", function (value) {
  return moment(value).fromNow();
});

// display toasts for async functions
window.toast = Toast;
